// =============================================================================
// src/data/atalaia-client.ts — Cliente runtime ANONIMO do backend AtalaIA (Hasura).
//
// SRP: wrapper fino do endpoint GraphQL (regra 9). DI por parametro (endpoint/
//   fetchImpl/timeoutMs), nunca global escondido. POST SEM `x-hasura-admin-secret`
//   (role anonima; secret no browser = vazamento — o build-time usa o admin secret
//   no script Node, ver scripts/atalaia-source.mjs). Timeout agressivo via
//   AbortController (lista defensiva do CLAUDE.md). Em qualquer falha LANCA com o
//   endpoint na mensagem (regra 11); o orquestrador de hidratacao captura e cai no
//   dataset estatico.
// =============================================================================

import type { AtalaiaClient, AtalaiaTerritoryRow } from '../app/types';
import { ATALAIA_QUERY, type AtalaiaGraphqlData } from './atalaia-mapping-query';

/** Dependencias injetaveis do cliente. */
export interface AtalaiaClientDeps {
  /** Endpoint GraphQL (ex.: http://localhost:8080/v1/graphql). */
  readonly endpoint: string;
  /** fetch injetavel p/ teste; default = fetch global do browser. */
  readonly fetchImpl?: typeof fetch;
  /** Timeout de rede em ms (default 6000). */
  readonly timeoutMs?: number;
}

/** Timeout padrao da requisicao ao vivo (defensivo: nao travar a tela). */
const DEFAULT_TIMEOUT_MS = 6000;

/** Envelope da resposta GraphQL do Hasura. */
interface AtalaiaGraphqlResponse {
  readonly data?: AtalaiaGraphqlData;
  readonly errors?: readonly unknown[];
}

/** Cria o cliente AtalaIA anonimo (implementa o contrato AtalaiaClient do DI). */
export function createAtalaiaClient(deps: AtalaiaClientDeps): AtalaiaClient {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  return { fetchLiveMunis: () => fetchLiveMunis(deps.endpoint, fetchImpl, timeoutMs) };
}

/** POST anonimo da ATALAIA_QUERY com timeout; devolve gold_territory ou lanca. */
async function fetchLiveMunis(
  endpoint: string,
  fetchImpl: typeof fetch,
  timeoutMs: number,
): Promise<readonly AtalaiaTerritoryRow[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetchImpl(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' }, // anon: SEM admin secret
      body: JSON.stringify({ query: ATALAIA_QUERY }),
      signal: controller.signal,
    });
    return await parseResponse(endpoint, res);
  } finally {
    clearTimeout(timer);
  }
}

/** Valida o HTTP + o envelope GraphQL; devolve as linhas ou lanca com contexto. */
async function parseResponse(
  endpoint: string,
  res: Response,
): Promise<readonly AtalaiaTerritoryRow[]> {
  if (!res.ok) throw new Error(`AtalaIA ${endpoint}: HTTP ${res.status}`);
  const json = (await res.json()) as AtalaiaGraphqlResponse;
  if (json.errors) throw new Error(`AtalaIA ${endpoint}: GraphQL ${JSON.stringify(json.errors)}`);
  if (!json.data) throw new Error(`AtalaIA ${endpoint}: resposta sem campo data`);
  return json.data.gold_territory;
}
